/// <amd-module name="WidgetCommon/CommonChart/CommonChartWidget" />
import * as React from "react";

import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";

import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, SemanticVersion, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import { ISemanticVersionService } from "Dashboards/WidgetContractServices/SemanticVersionService";

import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";

import * as ChartColor from "Tfs/Charting/ChartColor";
import { ChartBase, ChartProps, ChartTypeHelper } from "Tfs/Charting/ChartComponents";
import { IDataTable } from "Tfs/Charting/ChartContracts";
import { ChartTelemetry } from "Tfs/Charting/ChartTelemetry";
import { ChartConfiguration } from "Tfs/Charting/Contracts/Reporting";

import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";

import * as Resources from "WidgetCommon/CommonChart/Resources";
import { CommonChartConfigurationCacheableQuery } from "WidgetCommon/CommonChartCacheableQueries/CommonChartConfigurationCacheableQuery";
import { CommonChartResultCacheableQuery } from "WidgetCommon/CommonChartCacheableQueries/CommonChartResultCacheableQuery";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";

import * as TfsWidgetContext from "Tfs/Widget/Context";


export interface CommonChartWidgetProps extends WidgetProps {
    /** Link url to navigate to on click. */
    getLinkUrl: (chartConfiguration?: ChartConfiguration, queryText?: string) => string;

    /** Name of the feature, for retrieving query results. */
    featureName: string;

    /** Indicates whether user has permission to click. */
    hasClickPermission: boolean;

    /** Description for telemetry when the chart is clicked. Should describe what the user has clicked when navigation occurs from a chart click. */
    linkDescription: string;

    /** Navigation handler for the widget */
    handleNavigation?: (url: string) => void;

    /** Class name for styling */
    className?: string;

    openLightBox: (widgetProps: WidgetProps, layoutManager: IVssLayoutManager) => void;
}

export interface CommonChartWidgetState {
    configuration?: ChartConfiguration | undefined;
    results?: IDataTable;
}

/** Common legacy support component to render the user-customizable TFS Op-store charts widgets (TCM and WIT Charts). */
export class CommonChartWidget extends VssComponent<CommonChartWidgetProps, CommonChartWidgetState> {
    public static readonly TitleOffset: number = 40;

    constructor(props: CommonChartWidgetProps, context: IVssComponentContext) {
        super(props);
        this.state = {};
    }

    public async componentDidMount() {
        await this.initializeChartData();
        WidgetTelemetry.onWidgetLoaded(
            this.context.pageContext,
            this.props,
            this.state.configuration && ChartTelemetry.PackEssentialConfigurationForTelemetry(this.state.configuration)
        );
    }

    public async componentDidUpdate(prevProps: CommonChartWidgetProps, prevState: CommonChartWidgetState) {
        if (
            prevProps.settings !== this.props.settings ||
            prevProps.settingsVersion.major !== this.props.settingsVersion.major ||
            prevProps.size.height !== this.props.size.height ||
            prevProps.size.width !== this.props.size.width
        ) {
            await this.initializeChartData();
        }
        super.componentDidUpdate(prevProps, prevState);
    }

    public render(): JSX.Element | null {
        if (!this.props.settings) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        } else if (this.state.configuration) {
            const featureColorProvider = ChartColor.getFeatureColorProvider(this.state.configuration.scope);
            const colorDictionary = ChartColor.ColorDictionary.fromColorEntries(
                featureColorProvider,
                this.state.configuration.userColors,
                this.getSettingsVersion(this.props.settingsVersion).major
            );

            /**
             * Adjust width and height by consistent discrepancy between legacy and new widget sizing
             * to correct for overflowing legends
             */
            const legacyWidthDiscrepancy = 10;
            const legacyHeightDiscrepancy = 40;
            const adjustedWidth = this.props.size.width ? this.props.size.width - legacyWidthDiscrepancy : ChartBase.defaultWidth;
            const adjustedHeight = this.props.size.height ? this.props.size.height - legacyHeightDiscrepancy : ChartBase.defaultHeight;

            const chartBaseProps: ChartProps = {
                width: adjustedWidth,
                height: adjustedHeight,
                useCompactRenderMode: Math.min(this.props.size.width, this.props.size.height - CommonChartWidget.TitleOffset) <= 160,
                colorDictionary: colorDictionary,
                transformOptions: this.state.configuration.transformOptions,
                onLegendClick: this.onLegendClick,
                onClick: this.onClick,
                altTextExtraDescription: Resources.CommonChart_AltTextExtraDescription,
                accessibleTitle: this.state.configuration.title,
                suppressAnimation: !this.props.showLoadAnimations,
                dataset: this.state.results ? this.state.results.data : undefined
            };

            return (
                <div className={`common-chart-widget ${this.props.className}`}>
                    {ChartTypeHelper.getChartTypeComponent(chartBaseProps, this.state.configuration.chartType)}
                    { this.renderQueryLink()}
                </div>
            );
        } else {
            return null;
        }
    }

    private renderQueryLink(): JSX.Element {
        return (
            <FPSLink className={"view-query-link"} target={this.props.openLinksInNewTab ? "_blank" : undefined} onClick={this.onViewQueryClick}>
                {Resources.ViewQueryLinkText}
            </FPSLink>
        );
    }

    private async initializeChartData(): Promise<void> {
        try {
            let chartConfiguration = undefined;
            if (this.props.settings) {
                chartConfiguration = await this.getChartConfiguration();
                chartConfiguration.transformOptions.getFilteredQuery = true;
            }
            let results;
            if (chartConfiguration) {
                const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
                const resultCacheableQuery = new CommonChartResultCacheableQuery(
                    this.getProjectId(),
                    this.props.featureName,
                    chartConfiguration,
                    this.props.teamContext
                );
                results = await cacheableQueryService.getCacheableQueryResult(resultCacheableQuery);
            }
            this.setState(
                {
                    configuration: chartConfiguration,
                    results: results
                },
                this.props.onWidgetLoaded
            );
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
        }
    }

    private onLegendClick = () => {
        WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, this.buildTelemetryProperties());
    };

    private onClick = (openLinksInNewTab: boolean, index?: number, userClickedLabel?: string, isOtherTabClicked?: boolean) => {
        if (this.props.hasClickPermission) {
            if(isOtherTabClicked){
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                this.props.openLightBox(this.props, layoutManager);
            }
            else{
                userClickedLabel = (userClickedLabel && userClickedLabel === "(blank)") ? "[NULL]" : userClickedLabel;
                let queryText = (index != undefined && index > -1 && userClickedLabel) ? this.state.results?.data[index]?.value?.find(y => y.key === userClickedLabel)?.customData?.queryText : undefined;
                queryText = (queryText === undefined && index != undefined && index > -1) ? this.state.results?.data[0].value[index]?.customData?.queryText : queryText;
                const linkUrl = this.props.getLinkUrl(this.state.configuration, queryText);
                if (openLinksInNewTab) {
                    window.open(linkUrl, "_blank");
                } else {
                    if (this.props.handleNavigation) {
                        this.props.handleNavigation(linkUrl || linkUrl);
                    } else {
                        window.location.href = linkUrl || linkUrl;
                    }
                }
                WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, this.buildTelemetryProperties((queryText && queryText !== "") ? "FilteredQueryClick" : "MasterQueryClick"));
            }
        }
    };

    private onViewQueryClick = (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
        if (!event.isDefaultPrevented()) {
            this.onClick(event.ctrlKey);
        }
    };

    /* Returns a promise of a ChartConfiguration, sourced from either the customSettings or saved Chart Configuration */
    private async getChartConfiguration(): Promise<ChartConfiguration> {
        const parsedSettings: ChartConfiguration = JSON.parse(this.props.settings!);
        const majorVersion = this.getSettingsVersion(this.props.settingsVersion).major;
        if (majorVersion == 1 && parsedSettings.chartId) {
            const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
            const configurationCacheableQuery = new CommonChartConfigurationCacheableQuery(this.getProjectId(), parsedSettings.chartId);

            return cacheableQueryService.getCacheableQueryResult(configurationCacheableQuery);
        } else {
            /**
             *  When dealing with a configurable widget, always use the current widget Name as Chart title.
             **/
            parsedSettings.title = this.props.name;
            return Promise.resolve(parsedSettings);
        }
    }

    private getSettingsVersion(settingsVersion: SemanticVersion): SemanticVersion {
        if (settingsVersion) {
            return settingsVersion;
        } else {
            const semanticVersionService = this.context.pageContext.getService<ISemanticVersionService>("ISemanticVersionService");
            return semanticVersionService.getInitialVersion();
        }
    }

    // Changed this function to differentiate between clicks on the chart sections and clicks on the query link
    private buildTelemetryProperties(linkDescription: string = "") {
        return {
            linkDescription: (linkDescription === "") ? this.props.linkDescription : linkDescription
        };
    }

    private getProjectId(): string {
        return TfsWidgetContext.getProject(this.context.pageContext).id;
    }
}

VssComponent.register("commonChartWidget", CommonChartWidget);
